import { createContext, FunctionComponent, useContext, useEffect, useState } from 'react';
import { PartyBookingApiClient } from '../api/api.generated';
import { useApiClient } from '../hooks/useApiClient';
import useApiError from '../hooks/useApiError';
import { useCountries } from '../hooks/useCountries';
import { Error } from '../routes/Error';
import { useGlobalLoading } from './GlobalLoadingProvider';

interface LegacyBookingSystemContextValue {
    disabled: boolean;
}

const LegacyBookingSystemContext = createContext<LegacyBookingSystemContextValue>({} as LegacyBookingSystemContextValue);

export const LegacyBookingSystemProvider: FunctionComponent = ({ children }) => {
    const { incrementLoader, decrementLoader } = useGlobalLoading();
    const { apiError, setApiError, clearApiError } = useApiError();
    const { currentCountry } = useCountries();
    const partyApiClient = useApiClient(PartyBookingApiClient);
    const [disabled, setDisabled] = useState<boolean | undefined>();

    const load = () => {
        const controller = new AbortController();
        clearApiError();
        incrementLoader();
        setDisabled(undefined);

        partyApiClient
            .apiPartyBookingGetDisallowLegacyNewBookingsAndDateChangesByCountryCodeGet(currentCountry.code, controller.signal)
            .then(setDisabled)
            .catch((error) => {
                if (!controller.signal.aborted) {
                    setApiError(error);
                }
            })
            .finally(decrementLoader);

        return () => controller.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, []);

    if (apiError) {
        return <Error error={apiError} onRetryClick={load} />;
    }

    if (disabled === undefined) {
        return null;
    }

    return <LegacyBookingSystemContext.Provider value={{ disabled }}>{children}</LegacyBookingSystemContext.Provider>;
};

export const useLegacyBookingSystemDisabled = (): boolean => {
    return useContext(LegacyBookingSystemContext).disabled;
};
